import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ReactivationPrompt } from '@/components/auth/ReactivationPrompt'
import { useAuth } from '@/contexts/auth-context'
import { loginSchema, type LoginFormData } from '@/lib/validation'
import { toast } from '@/hooks/use-toast'

interface TestUser {
  id: number
  name: string
  email: string
  password: string
  organizationRole: string
  teamRole?: string
  position: string
  emoji: string
  color: string
}

const testUsers: TestUser[] = [
  // Organization roles
  { id: 1, name: 'Sarah Johnson', email: 'owner@acme.com', password: 'password123', organizationRole: 'owner', position: 'CEO • Owner', emoji: '👑', color: 'bg-amber-100 border-amber-200 hover:bg-amber-50' },
  { id: 2, name: 'Michael Chen', email: 'admin@acme.com', password: 'password123', organizationRole: 'admin', position: 'Head of Ops • Admin', emoji: '🏢', color: 'bg-purple-100 border-purple-200 hover:bg-purple-50' },

  // Staff with team roles
  { id: 3, name: 'Emily Rodriguez', email: 'manager@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'manager', position: 'Engineering Manager', emoji: '👥', color: 'bg-blue-100 border-blue-200 hover:bg-blue-50' },
  { id: 4, name: 'David Kim', email: 'teamlead@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'lead', position: 'Senior Engineer • Lead', emoji: '⭐', color: 'bg-cyan-100 border-cyan-200 hover:bg-cyan-50' },
  { id: 5, name: 'Alice Brown', email: 'alice@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'member', position: 'Software Engineer', emoji: '💻', color: 'bg-green-100 border-green-200 hover:bg-green-50' },
  { id: 6, name: 'Bob Wilson', email: 'bob@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'member', position: 'Frontend Developer', emoji: '🎨', color: 'bg-indigo-100 border-indigo-200 hover:bg-indigo-50' },
  { id: 7, name: 'Carol Davis', email: 'carol@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'member', position: 'Backend Developer', emoji: '⚙️', color: 'bg-rose-100 border-rose-200 hover:bg-rose-50' },
  { id: 8, name: 'Daniel Garcia', email: 'daniel@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'member', position: 'DevOps Engineer', emoji: '🔧', color: 'bg-orange-100 border-orange-200 hover:bg-orange-50' },
  { id: 9, name: 'Eva Martinez', email: 'eva@acme.com', password: 'password123', organizationRole: 'staff', teamRole: 'member', position: 'QA Engineer', emoji: '✅', color: 'bg-emerald-100 border-emerald-200 hover:bg-emerald-50' }
]

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isDevMode, setIsDevMode] = useState(false)
  const [reactivationState, setReactivationState] = useState<{ email: string; password: string; message?: string } | null>(null)
  const { login } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  useEffect(() => {
    // Check if we're in development mode
    setIsDevMode(process.env.NODE_ENV === 'development')
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)

    try {
      const loggedInUser = await login(data.email, data.password)
      toast({
        title: 'Welcome back!',
        description: 'You have been successfully logged in.',
      })
      if (loggedInUser && !loggedInUser.onboardingCompleted) {
        navigate('/onboarding')
      } else {
        navigate('/dashboard')
      }
    } catch (error) {
      const authError = error as Error & { code?: string; data?: any }

      if (authError?.code === 'ACCOUNT_DEACTIVATED') {
        if (authError.data?.canReactivate) {
          setReactivationState({
            email: data.email,
            password: data.password,
            message:
              authError.message ||
              'Your account has been deactivated. Reactivate to regain access to your organization.',
          })
          return
        }

        const inactiveReason = authError.message ||
          'Your organization has been suspended by its owner. Please contact them for assistance.'

        toast({
          variant: 'destructive',
          title: 'Sign-in blocked',
          description: inactiveReason,
        })
        return
      }

      toast({
        variant: 'destructive',
        title: 'Login failed',
        description: authError?.message || 'Invalid email or password. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleTestUserSelect = (user: TestUser) => {
    setValue('email', user.email)
    setValue('password', user.password)
  }

  const handleSocialLogin = (provider: 'google' | 'linkedin') => {
    const configuredApiUrl = import.meta.env.VITE_API_URL as string | undefined
    const backendBaseUrl = configuredApiUrl
      ? configuredApiUrl.replace(/\/?api\/?$/, '')
      : 'http://localhost:3000'

    const callbackUrl = `${window.location.origin}/dashboard`
    const signinUrl = `${backendBaseUrl}/api/auth/signin/${provider}?callbackUrl=${encodeURIComponent(callbackUrl)}`

    window.location.href = signinUrl
  }

  return (
    <div className="w-full max-w-md">
      {reactivationState && (
        <ReactivationPrompt
          email={reactivationState.email}
          password={reactivationState.password}
          message={reactivationState.message}
          onCancel={() => setReactivationState(null)}
        />
      )}

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Welcome back</CardTitle>
          <CardDescription>
            Enter your email and password to sign in to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  {...register('password')}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                />
                <label
                  htmlFor="remember-me"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Remember me
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => handleSocialLogin('google')}
                className="w-full"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Google
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSocialLogin('linkedin')}
                className="w-full"
              >
                <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                    clipRule="evenodd"
                  />
                </svg>
                LinkedIn
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <div className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-foreground hover:underline"
              >
                Sign up
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Development Mode Test Credentials */}
      {isDevMode && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">🧪 Test Credentials</CardTitle>
            <CardDescription>
              Click any user below to auto-fill login credentials (Development Mode)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {testUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleTestUserSelect(user)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors text-left ${user.color}`}
                >
                  <span className="text-lg">{user.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{user.name}</span>
                      <span className="text-xs px-2 py-1 bg-white/50 rounded-full">
                        {user.organizationRole}{user.teamRole ? ` • ${user.teamRole}` : ''}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {user.email}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                💡 <strong>Quick Login:</strong> Click any user card above to auto-fill credentials and test different user roles
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
